// docs/41 in the encounter HUD: the puzzle room's card. The clues the party holds and where each
// came from, the lock's pieces as the board labels them, what the lock said to every try, and the
// hint shop that spends the room's XP on credit. It never says how the room works (PZ11): the stage
// and lock rules stay off the card, and the objective sits atop the side panel. Every line is the
// server's (`puzzle_room`, `puzzle_note`); the prices are reckoned as `buy_hint` reckons them
// (core/puzzle/hints.ts).
import { useEffect, useRef, useState } from 'react';
import { Badge, Button, confirmDialog } from '../components';
import { useEncounter, useShownSnapshot } from '../../core/encounter/session';
import { useEncounterSecured, usePuzzleNotes, usePuzzleRoom } from '../../core/subscriptions/hooks';
import { rpc } from '../../core/net/reducers';
import { usePuzzle } from '../../core/stores/puzzle';
import { HINTS, HINT_FORCE, hintBlocked, hintPrice, rewardNow } from '../../core/puzzle/hints';
import { seenPieces } from '../../core/puzzle/pieces';

/** `puzzle_note.kind`. */
const NOTE = { map: 0, inscription: 1, nudge: 2, recall: 3, step: 4, force: 5, tried: 6 } as const;
/** Clues the party turned up (a recalled one was bought, but it is a clue all the same). */
const CLUE_KINDS: readonly number[] = [NOTE.map, NOTE.inscription, NOTE.recall];
/** Hints bought that are not clues: a nudge, a step, the gate forced. */
const HINT_KINDS: readonly number[] = [NOTE.nudge, NOTE.step, NOTE.force];
const WHENCE: Record<number, string> = { [NOTE.map]: 'found on the way', [NOTE.inscription]: 'read in the room', [NOTE.recall]: 'recalled for XP' };
const GENRE_WORD: Record<string, string> = { Choose: 'Statues', Order: 'Runes', Bells: 'Bells', Pattern: 'Levers', Assign: 'Dials', Witness: 'Witnesses', Gaze: 'Gazes', Pairs: 'Pairs', Tally: 'Dials', Offering: 'Offerings' };
const COUNT = ['No', 'One', 'Two', 'Three', 'Four', 'Five', 'Six'];

/** Hovering something with a fixture lights it on the board; leaving puts the light out. */
function focusProps(fixture: number | undefined) {
  if (fixture === undefined) return {};
  return {
    onMouseEnter: () => usePuzzle.getState().setFocus(fixture, 'hover'),
    onMouseLeave: () => { const s = usePuzzle.getState(); if (s.source === 'hover' && s.focus === fixture) s.setFocus(undefined); },
  };
}

export function PuzzlePanel() {
  const encounterId = useEncounter((s) => s.encounterId);
  const room = usePuzzleRoom(encounterId);
  const shown = useShownSnapshot();
  const closedDoors = !!shown?.fog?.veils.some((v) => v.key === 'rooms');
  const fixtureNames = closedDoors ? (shown?.fixtures ?? []).map((f) => f.name) : undefined;
  const notes = usePuzzleNotes(encounterId);
  // docs/41: once the way is open the room is solved (or forced) and sells nothing more
  const solved = !!useEncounterSecured(encounterId);
  const [open, setOpen] = useState(true);
  const [busy, setBusy] = useState<number>();
  // a new nudge or step lights what it is about (and stays lit until something else takes the light)
  const lastHint = [...notes].reverse().find((n) => (n.kind === NOTE.nudge || n.kind === NOTE.step) && n.fixture !== undefined);
  const lit = useRef<bigint | undefined>(undefined);
  useEffect(() => {
    if (!lastHint || lit.current === lastHint.id) return;
    lit.current = lastHint.id;
    usePuzzle.getState().setFocus(lastHint.fixture!, 'hint');
  }, [lastHint]);
  // docs/41 PZ8: the board draws the room's workings only around what a bought hint named
  const pointed = (kinds: readonly number[]) => notes.filter((n) => kinds.includes(n.kind) && n.fixture !== undefined).map((n) => n.fixture!).join(',');
  const hinted = pointed([NOTE.nudge, NOTE.step]), stepped = pointed([NOTE.step]);
  useEffect(() => {
    const ids = (s: string) => new Set(s ? s.split(',').map(Number) : []);
    usePuzzle.getState().setHinted(ids(hinted), ids(stepped));
  }, [hinted, stepped]);
  useEffect(() => () => usePuzzle.getState().setHinted(new Set(), new Set()), []);
  if (!room || encounterId === undefined) return null;

  const clues = notes.filter((n) => CLUE_KINDS.includes(n.kind));
  const held = new Set(clues.map((n) => n.clue).filter((c): c is number => c !== undefined)).size;
  const tried = notes.filter((n) => n.kind === NOTE.tried);
  const hints = notes.filter((n) => HINT_KINDS.includes(n.kind));
  const reward = room.rewardXp, debt = room.debtXp;
  const missing = room.clueTotal - held;
  const pieces = seenPieces(room.pieces, fixtureNames);
  const buy = async (kind: number) => {
    if (kind === HINT_FORCE && !(await confirmDialog({
      eyebrow: 'Force the gate', title: 'Break the way open?',
      text: `The way gives at the end of this turn. The room then pays only ${rewardNow(reward, reward)} XP each instead of ${rewardNow(reward, debt)}.`,
      confirmLabel: 'Force it', danger: true,
    }))) return;
    setBusy(kind);
    await rpc.buyHint(encounterId, kind);
    setBusy(undefined);
  };

  return (
    <section className="fk-hud__puzzle fk-hit" data-testid="puzzle-panel" data-genre={room.genre} data-clues={held} data-clue-total={room.clueTotal} aria-label="The puzzle room">
      <button type="button" className="fk-hud__puzzle-head" onClick={() => setOpen(!open)} aria-expanded={open} data-testid="puzzle-toggle">
        <h3 className="fk-hud__label">{room.place || 'Puzzle'} · {GENRE_WORD[room.genre] ?? room.genre}</h3>
        <span className="fk-hud__puzzle-count">{held} / {room.clueTotal} clues</span>
        <span aria-hidden="true">{open ? '▾' : '▸'}</span>
      </button>
      {open && <>
        {/* docs/57: what the storyteller saw here */}
        {room.scene && <p className="fk-hud__puzzle-scene" data-testid="puzzle-scene">{room.scene}</p>}

        {/* the party's memory: every clue it has turned up, free — the room never says how they fit */}
        <h4 className="fk-hud__puzzle-sub">Clues <Badge tone={missing ? 'grey' : 'gold'}>{held} of {room.clueTotal}</Badge></h4>
        {clues.length > 0
          ? <ul className="fk-hud__puzzle-clues" data-testid="puzzle-clues">
            {clues.map((n) => (
              <li key={n.id.toString()} className="fk-hud__puzzle-clue" data-testid="puzzle-clue" data-kind={n.kind} {...focusProps(n.fixture)}>
                <span className="fk-hud__puzzle-whence">{WHENCE[n.kind]}</span>
                {n.text}
              </li>
            ))}
          </ul>
          : null}
        {(missing > 0 || !clues.length) && (
          <p className="fk-hud__puzzle-tally" data-testid="puzzle-missing">
            {!clues.length && 'None found yet. '}{missing > 0 && `${COUNT[missing] ?? missing} ${clues.length ? 'more ' : ''}to find.`}
          </p>
        )}

        <h4 className="fk-hud__puzzle-sub">{GENRE_WORD[room.genre] ?? 'Pieces'}</h4>
        {/* docs/63: behind a shut door the pieces are not known yet */}
        {!pieces.length && <p className="fk-hud__puzzle-tally" data-testid="puzzle-pieces-unseen">Not found yet.</p>}
        <ul className="fk-hud__puzzle-pieces" data-testid="puzzle-pieces" data-seen={pieces.length}>
          {pieces.map((p) => {
            // docs/55: a witness's words come third ("the heron statue — bronze, holding a lamp — The heron says: …")
            const [name, look, says] = p.split(' — ');
            return <li key={p}><b>{name}</b>{look && <span> — {look}</span>}{says && <q className="fk-hud__puzzle-says">{says.replace(/^The .+? says: /, '').replace(/^“|”$/g, '')}</q>}</li>;
          })}
        </ul>

        {tried.length > 0 && <>
          <h4 className="fk-hud__puzzle-sub">What you tried</h4>
          <ul className="fk-hud__puzzle-log">
            {tried.slice(-6).map((n) => (
              <li key={n.id.toString()} className={`fk-hud__puzzle-note fk-hud__puzzle-note--k${n.kind}`} data-testid="puzzle-note" data-kind={n.kind} {...focusProps(n.fixture)}>{n.text}</li>
            ))}
          </ul>
        </>}

        {/* hints are bought, never given: kept apart from the clues the party found */}
        <h4 className="fk-hud__puzzle-sub">Hints</h4>
        {hints.length > 0 && (
          <ul className="fk-hud__puzzle-log" data-testid="puzzle-hints">
            {hints.map((n) => (
              <li key={n.id.toString()} className={`fk-hud__puzzle-note fk-hud__puzzle-note--k${n.kind}`} data-testid="puzzle-note" data-kind={n.kind} {...focusProps(n.fixture)}>
                {n.text}
                {n.cost > 0 && <span className="fk-hud__puzzle-cost"> · {n.by} paid {n.cost} XP each</span>}
              </li>
            ))}
          </ul>
        )}
        <div className="fk-hud__puzzle-reward" data-testid="puzzle-reward" data-reward={reward} data-debt={debt}>
          Reward: <b>{rewardNow(reward, debt)} XP</b> each{debt > 0 && <> · {debt} spent on hints</>}{room.par > 0 && <> · par {room.par} turns</>}
        </div>
        <div className="fk-hud__puzzle-shop" role="group" aria-label="Hints, paid from the room's reward">
          {HINTS.map((h) => {
            // docs/72: a hint scroll used in camp makes the party's next hint free (`puzzle_room.free_hints`)
            const free = h.kind !== HINT_FORCE && (room.freeHints ?? 0) > 0;
            const why = solved ? 'The way is open' : free ? hintBlocked(h.kind, Number.MAX_SAFE_INTEGER, 0, room.forced, held, room.clueTotal) : hintBlocked(h.kind, reward, debt, room.forced, held, room.clueTotal);
            const price = free ? 0 : hintPrice(h.kind, reward, debt);
            return (
              <Button key={h.kind} small variant={h.kind === HINT_FORCE ? 'danger' : free ? 'primary' : 'secondary'} disabled={!!why} loading={busy === h.kind}
                title={why ?? (free ? `${h.what} — free (${room.freeHints} free hint${room.freeHints === 1 ? '' : 's'} from a scroll)` : `${h.what} — ${price} XP each`)} data-testid={`puzzle-hint-${h.kind}`} data-free={free || undefined} onClick={() => void buy(h.kind)}>
                {h.name} · {free ? 'free' : `−${price} XP`}
              </Button>
            );
          })}
        </div>
      </>}
    </section>
  );
}
